import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError } from '../api';
import { Banner, Field } from '../components/ui';

type Mode = 'superuser' | 'user';

export function Login() {
  const { session, loading, loginSuperuser, loginUser } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('superuser');
  const [applicationId, setApplicationId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="empty">Checking your session…</div>;
  if (session) {
    const home = session.kind === 'superuser' ? '/admin' : '/app';
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? home} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'superuser') await loginSuperuser(email, password);
      else await loginUser(applicationId.trim(), email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-card__title">IndiaHMIS Auth</h1>
        <p className="auth-card__sub">auth.indiahmis.com</p>

        <div className="tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'superuser'}
            className={`tab ${mode === 'superuser' ? 'tab--active' : ''}`}
            onClick={() => {
              setMode('superuser');
              setError(null);
            }}
          >
            Superadmin
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'user'}
            className={`tab ${mode === 'user' ? 'tab--active' : ''}`}
            onClick={() => {
              setMode('user');
              setError(null);
            }}
          >
            Application user
          </button>
        </div>

        <form onSubmit={onSubmit} className="stack">
          {error && <Banner kind="error">{error}</Banner>}

          {mode === 'user' && (
            <Field label="Application ID" hint="Sent to you with your account credentials.">
              <input
                className="input"
                required
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                autoComplete="off"
              />
            </Field>
          )}

          <Field label="Email">
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              placeholder={mode === 'superuser' ? 'admin@indiahmis.com' : 'you@example.com'}
            />
          </Field>

          <Field label="Password">
            <input
              className="input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>

          <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth-card__foot">
          {mode === 'superuser'
            ? 'Superadmins manage applications and other superadmins.'
            : 'Master users manage permissions, roles and users for their application.'}
        </p>
      </div>
    </div>
  );
}
